import { List, Tag } from "antd";
import CustomBreadCrumb from "components/CustomBreadCrumb/CustomBreadCrumb";
import PageLayout from "layout/PageLayout/PageLayout";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import centralApi from "services/centralApi";
import "./CrashDetailedView.css";
import { API_ENDPOINTS, ROUTES } from "utils/constants";
import { ApmApiMethods, DateFormatEnum } from "utils/enums";
import { t } from "i18next";
import dayjs from "dayjs";
import Selector from "components/Selector/Selector";
interface CrashDetailedViewProps {
  ids?: string[];           // array of event IDs
  time?: string;
}

const CrashDetailedView: FC<CrashDetailedViewProps> = () => {
  const location = useLocation();
  const { ids = [], time } = location.state || {};
  const [selectedId, setSelectedId] = useState(ids?.[0].id);

  const loginUser = useSelector((state: any) => state.loginUser.data);
  const { appId } = useSelector((state: any) => state.activeApp);
  const queryParams = new URLSearchParams(location.search);
  const currentItem = ids.find((item: any) => item.id === selectedId);
const currentTime = currentItem?.dateCreated;
  const [issue, setIssue] = useState<{
    data: {
      frames: any[];
      title: string;
      eventID: string;
      metadata: { function: string; value: string; type?: string };
      contexts: any;
      dist:string
    };
    loading: boolean;
  }>({
    data: {
      frames: [],
      title: "",
      eventID: "",
      metadata: { function: "", value: "" },
      contexts: {},
      dist:""
    },
    loading: true,
  });

    const getIssue = async () => {
    try {
      setIssue((prevState) => ({ ...prevState, loading: true }));
      const params = {
        api_key: loginUser.api_key,
        app_id: appId,
        id:selectedId,
        api_method: ApmApiMethods.EVENTS,
      };
      const data= await centralApi(
        "POST",
        API_ENDPOINTS.APM_METRICS_URL,
        params
      );

      const stack_trace_frames =
        data?.entries?.[0]?.data?.values?.[0]?.stacktrace?.frames;
      setIssue((prevState) => ({
        ...prevState,
        data: {
          frames: stack_trace_frames || [],
          title: data.title,
          eventID: data.eventID,
          metadata: data.metadata,
          contexts: data.contexts,
          dist:data.dist
        },
        loading: false,
      }));
    } catch (error) {
      setIssue((prevState) => ({
        ...prevState,
        data: {
          frames: [],
          eventID: "",
          title: "",
          metadata: { function: "", value: "" },
          contexts: {},
          dist:""
        },
        loading: false,
      }));
    }
  };
  useEffect(() => {
    if (selectedId) {
      getIssue();
    }
  }, [selectedId]);

  return (
    <PageLayout
      className="crash-detailed-view-container"
      title={`${t("analyzeLabel")}`}
      topLeftItem={
        <CustomBreadCrumb
          items={[
            {
              title: (
                <Link to={ROUTES.DASHBOARD_ANALYZE_CRASHES}>
                  {t("issuesLabel")}
                </Link>
              ),
            },
            {
              title: <span>{t("viewLabel")}</span>,
            },
          ]}
        />
      }
    >
      <div className="mb-4 d-flex justify-content-between">
        <div>
          <strong>{t("stackTraceLabel")}</strong>
          <div className="d-flex gap-2 mt-2 align-items-start">
            <strong>{t("eventIdLabel")}</strong>
            <span>{issue.data.eventID}</span>
          </div>

          <Tag color="red" className="d-flex flex-column gap-1 mt-3 ">
            <strong>
              {issue.data?.metadata?.type || issue.data?.metadata?.function}
            </strong>
            <span
              className="text-overflow-ellipsis crash-metadata-value"
            >
              {issue.data?.metadata?.value}
            </span>
          </Tag>
        </div>
        <div>
          {currentTime && (
            <span>
              {dayjs(currentTime).format(DateFormatEnum.FULL_MONTH_TIME)}
            </span>
          )}
        </div>
      </div>
      <div className="row">
      <Selector
      className="mb-2 crash-selector-300"
  value={selectedId}
  onChange={(value) => setSelectedId(value)}
  options={ids.map((item:any) => ({
    label: item.id,  
    value: item.id,  
  }))}
/>

        <div className="col-9">
          <List
            loading={issue.loading}
            itemLayout="vertical"
            className="crash-stacktrace-list"
            dataSource={issue.data?.frames?.reverse()}
            renderItem={(item) => (
              <div className="crash-stacktrace-frame  rounded d-flex justify-content-between  p-2">
                <div className="d-flex gap-2 text-overflow-ellipsis">
                  <span className="text-overflow-ellipsis">
                    {item.filename || item.module}
                  </span>
                  {item.function ? (
                    <div className="d-flex gap-2">
                      <span className="text-muted">{t("inLabel")}</span>
                      <span>{item.function}</span>
                    </div>
                  ) : (
                    <span>{t("anonymousLabel")}</span>
                  )}
                  {item.lineNo ? (
                    <span className="text-muted">{t("onLineLabel")}</span>
                  ) : (
                    <></>
                  )}
                  {item.lineNo}
                  {item.colNo ? `:${item.colNo}` : ""}{" "}
                </div>
                <div>
                  {item.inApp ? (
                    <Tag color={"green"}>{t("inappLabel")}</Tag>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            )}
          />
        </div>

        <div className="col-3 border-start crash-detailed-view-right-container">
          {[
            {
              label: t("appNameLabel"),
              value: issue.data?.contexts?.app?.app_name,
            },
            {
              label: t("appVersion"),
              value: issue.data?.contexts?.app?.app_version,
            },
            {
              label: t("bundleIdLabel"),
              value: issue.data?.contexts?.app?.app_identifier,
            },
            { label: t("osNameLabel"), value: issue.data?.contexts?.os?.name },
            { label: t("osLabel"), value: issue.data?.contexts?.os?.os },
            {
              label: t("osVersionLabel"),
              value: issue.data?.contexts?.os?.version,
            },
            {
              label: t("kernelVersion"),
              value: issue.data?.contexts?.os?.kernel_version,
            },
             {
              label: t("buildLabel"),
              value: issue.data?.dist
            },
          ].map(
            ({ label, value }, index) =>
              value && (
                <p key={index}>
                  <strong>{label}</strong> {value}
                </p>
              )
          )}
        </div>
        <div className="col-12 mt-3">
        <CodeViewer context={issue.data?.frames?.[0]?.context} lineNo={issue.data?.frames?.[0]?.lineNo} colNo={issue.data?.frames?.[0]?.colNo} />
        </div>
      </div>
    </PageLayout>
  );
};

export default CrashDetailedView;






const CodeViewer = ({ context, lineNo, colNo }:{context:any[],lineNo:number,colNo:number}) => {
  return (
    context?.length?<div className="code-snippet-main-container w-100">
      {context.map(([num, ...parts]) => {
        const codeLine = parts.join("");
        const isErrorLine = num === lineNo;

        return (
          <div
            key={num}
            className={`code-line ${isErrorLine ? "error-line" : ""}`}
          >
            {/* Line Number */}
            <span className="line-number">{num}</span>

            {/* Code Content */}
            <pre className="code-text">
              {isErrorLine ? (
                <>
                  {codeLine.substring(0, colNo - 1)}
                  <span className="error-char">
                    {codeLine?.[colNo - 1]}
                  </span>
                  {codeLine.substring(colNo)}
                </>
              ) : (
                codeLine
              )}
            </pre>
          </div>
        );
      })}
    </div>:<></>
  );
};



